import { useCallback, useMemo, useState, useEffect } from 'react';

import { StorageItem } from '@page/core/lib/web/storage';
import { SameWindowStorageChangeEvent, sameWindowStorageChangeEventBus } from '@page/core/lib/web/storage/storage.lib';

type StorageItemOrKey<T = string> = string | StorageItem<T>;

export const useStorageAsState = <T = string>(storageItemOrKey: StorageItemOrKey<T>) => {
  const storageItem = useMemo(() => {
    if (typeof storageItemOrKey === 'string') return new StorageItem<T>(storageItemOrKey);

    return storageItemOrKey;
  }, [storageItemOrKey]);

  const [value, setValue] = useState<T | null>(() => storageItem.get());

  const set = useCallback(
    (newValue: T) => {
      storageItem.set(newValue);
      setValue(newValue);
    },
    [storageItem],
  );

  const remove = useCallback(() => {
    storageItem.remove();
    setValue(null);
  }, [storageItem]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent | SameWindowStorageChangeEvent) => {
      if (event.key !== storageItem.key) return;

      const currentValue = storageItem.get();

      setValue(currentValue);
    };

    // Listen for both cross-window and same-window storage changes
    window.addEventListener('storage', handleStorageChange);
    const unsubscribe = sameWindowStorageChangeEventBus.subscribe(handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      unsubscribe();
    };
  }, [storageItem]);

  return [value, set, remove] as const;
};
